/**
 * Command box with the confirmation line above it (ATC-SIM puts messages "on the top of
 * the command area"), command history (Up/Down), and a text panel for STATS/HELP output.
 */
export interface CommandBar {
  /** Put a callsign in the box (from a strip or blip click) and focus it. */
  prefill(callsign: string): void;
  setMessage(text: string, ok?: boolean): void;
  showText(title: string, text: string): void;
  hideText(): void;
  focus(): void;
  /** Off while a menu is open: submissions are ignored and typing elsewhere no longer focuses the box. */
  setEnabled(enabled: boolean): void;
  destroy(): void;
}

export function createCommandBar(container: HTMLElement, onSubmit: (line: string) => void): CommandBar {
  container.classList.add('rs-command');
  container.innerHTML = `<div class="rs-message" aria-live="polite"></div><form class="rs-form"><span class="rs-prompt">&gt;</span><input class="rs-input" type="text" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="UAL123 C 040 L 28R" /></form><div class="rs-textpanel" hidden><div class="rs-textpanel-title"></div><pre></pre><button type="button">close</button></div>`;
  const message = container.querySelector<HTMLElement>('.rs-message')!;
  const form = container.querySelector<HTMLFormElement>('.rs-form')!;
  const input = container.querySelector<HTMLInputElement>('.rs-input')!;
  const panel = container.querySelector<HTMLElement>('.rs-textpanel')!;
  const history: string[] = [];
  let hIdx = -1;
  let enabled = true;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const line = input.value.trim();
    if (!line || !enabled) return;
    history.push(line); hIdx = history.length;
    onSubmit(line);
    input.value = '';
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); if (hIdx > 0) { hIdx--; input.value = history[hIdx] ?? ''; } }
    else if (e.key === 'ArrowDown') { e.preventDefault(); if (hIdx < history.length - 1) { hIdx++; input.value = history[hIdx] ?? ''; } else { hIdx = history.length; input.value = ''; } }
    else if (e.key === 'Escape') { input.value = ''; api.hideText(); }
  });
  panel.querySelector('button')!.addEventListener('click', () => api.hideText());
  // Typing anywhere focuses the box (like ATC-SIM where the box is always live).
  const globalKey = (e: KeyboardEvent) => {
    if (!enabled) return;
    if (e.target === input || e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target as HTMLElement;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return;
    if (e.key.length === 1) { input.focus(); }
  };
  document.addEventListener('keydown', globalKey);

  const api: CommandBar = {
    prefill(cs) { input.value = `${cs} `; input.focus(); input.setSelectionRange(input.value.length, input.value.length); },
    setMessage(text, ok = true) { message.textContent = text; message.classList.toggle('error', !ok); },
    showText(title, text) { panel.querySelector('.rs-textpanel-title')!.textContent = title; panel.querySelector('pre')!.textContent = text; panel.hidden = false; },
    hideText() { panel.hidden = true; },
    focus() { input.focus(); },
    setEnabled(v) { enabled = v; input.disabled = !v; },
    destroy() { document.removeEventListener('keydown', globalKey); container.replaceChildren(); container.classList.remove('rs-command'); },
  };
  return api;
}
